import { useId } from 'react';
import type { PurchaseLineView } from '@obraplan/core/application/contracts/purchase-lines';
import { describeDeadline } from '@obraplan/core/domain/schedule/material-alert';
import { formatShortRange } from '@/features/schedule-items/schedule-items-model';
import { cn } from '@/lib/cn';
import { formatDayMonth, formatDayMonthName } from '@/lib/format';
import {
  groupByWeek,
  weekStatus,
  weeksBetween,
  type MaterialDeadline,
  type MaterialWeek,
} from './materials-timeline';
import { BASIS_HELP, ORIGIN_LABEL, formatPurchaseQuantity } from './purchase-model';

/** Quantas linhas a lista desenha de uma vez; além disso, a tela pede um filtro. */
export const MAX_VISIBLE_LINES = 1500;

export interface MaterialsTimelineProps {
  /** As linhas do filtro, todas: a semana precisa da obra inteira para contar. */
  readonly lines: readonly PurchaseLineView[];
  /** Hoje (`AAAA-MM-DD`): divide as semanas em passadas, a de hoje e as que vêm. */
  readonly today: string;
  readonly deadline: (line: Pick<PurchaseLineView, 'id' | 'requiredDate'>) => MaterialDeadline;
  /** A resposta é de um filtro anterior: a lista esmaece até a nova chegar. */
  readonly stale?: boolean;
}

/**
 * A vista de materiais como linha do tempo (redesenho de 21/10/2026).
 *
 * Em cima, o **ritmo de chegada**: uma barra por semana com quantos materiais precisam
 * chegar nela — as que passaram em vermelho, a de hoje em azul, as do horizonte em âmbar.
 * Clicar numa semana rola até ela. Embaixo, a lista **por semana de necessidade**: a data e o
 * prazo na cor do estado, o material, onde ele cai e o código, a quantidade e a janela do
 * serviço. Uma linha por material, sem selo repetido: o código provisório é o próprio código,
 * em âmbar.
 *
 * Cor nunca é o único sinal (PLANO_GERAL §4.4): o prazo está escrito ("venceu há 15 dias"),
 * e cada barra diz em palavras quantos materiais tem.
 */
export function MaterialsTimeline({
  lines,
  today,
  deadline,
  stale = false,
}: MaterialsTimelineProps) {
  const prefix = useId();
  const visible = lines.length > MAX_VISIBLE_LINES ? lines.slice(0, MAX_VISIBLE_LINES) : lines;
  const weeks = groupByWeek(visible, today);
  const anchorOf = (weekStart: string): string => `${prefix}-semana-${weekStart}`;

  return (
    <div className={cn('flex flex-col gap-5', stale && 'opacity-60')} aria-busy={stale}>
      <Rhythm weeks={weeks} today={today} deadline={deadline} anchorOf={anchorOf} />
      <div className="flex flex-col border-t border-(--color-border-subtle)">
        {weeks.map((week) => (
          <WeekGroup key={week.start} week={week} deadline={deadline} id={anchorOf(week.start)} />
        ))}
      </div>
      {lines.length > visible.length && (
        <p role="note" className="text-xs text-(--color-content-muted)">
          Mostrando as {MAX_VISIBLE_LINES.toLocaleString('pt-BR')} primeiras linhas de{' '}
          {lines.length.toLocaleString('pt-BR')}. Filtre por torre, pavimento ou serviço para ver o
          resto.
        </p>
      )}
    </div>
  );
}

/** Uma barra por semana, da primeira à última com material, as vazias incluídas. */
function Rhythm({
  weeks,
  today,
  deadline,
  anchorOf,
}: {
  readonly weeks: readonly MaterialWeek[];
  readonly today: string;
  readonly deadline: MaterialsTimelineProps['deadline'];
  readonly anchorOf: (weekStart: string) => string;
}) {
  const first = weeks[0];
  const last = weeks.at(-1);
  if (first === undefined || last === undefined) return null;
  const byStart = new Map(weeks.map((week) => [week.start, week]));
  const ruler = weeksBetween(first.start, last.start);
  const max = Math.max(1, ...weeks.map((week) => week.lines.length));
  const currentWeek = ruler.find((start) => weekStatus(start, today) === 'CURRENT');

  const scrollTo = (weekStart: string): void => {
    document.getElementById(anchorOf(weekStart))?.scrollIntoView({ block: 'start' });
  };

  return (
    <section aria-label="Ritmo de chegada por semana" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs text-(--color-content-muted)">
        <span>
          Quantos materiais precisam chegar em cada semana. Clique numa semana para ir até ela.
        </span>
        <span className="font-condensed">
          {ruler.length} {ruler.length === 1 ? 'semana' : 'semanas'}
        </span>
      </div>
      <div
        role="group"
        aria-label="Semanas"
        className="flex h-18 items-end gap-1 border-b border-(--color-border-subtle)"
      >
        {ruler.map((start) => {
          const week = byStart.get(start);
          const count = week?.lines.length ?? 0;
          const status = weekStatus(start, today);
          const dueSoon =
            week !== undefined && week.lines.some((line) => deadline(line).level === 'DUE_SOON');
          const tone =
            status === 'PAST'
              ? 'bg-(--color-state-danger) opacity-90'
              : status === 'CURRENT'
                ? 'bg-(--color-brand)'
                : dueSoon
                  ? 'bg-(--color-state-warn) opacity-85'
                  : 'bg-(--color-content-muted) opacity-50';
          return (
            <button
              key={start}
              type="button"
              disabled={week === undefined}
              onClick={() => scrollTo(start)}
              aria-label={`Semana de ${formatDayMonthName(start)}: ${String(count)} ${count === 1 ? 'material' : 'materiais'}`}
              className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1 rounded-t px-0 text-(--color-content-muted) enabled:hover:bg-(--color-surface-muted) disabled:cursor-default"
            >
              <span className="font-condensed text-[11px] leading-none">
                {count > 0 ? count : ''}
              </span>
              <span
                aria-hidden="true"
                className={cn('w-full max-w-9 rounded-t-sm', tone)}
                style={{
                  height: `${String(Math.max(count > 0 ? 4 : 1, Math.round((count / max) * 44)))}px`,
                }}
              />
            </button>
          );
        })}
      </div>
      <div className="flex gap-1" aria-hidden="true">
        {ruler.map((start) => (
          <span
            key={start}
            className={cn(
              'font-condensed min-w-0 flex-1 truncate text-center text-[11px] text-(--color-content-muted)',
              start === currentWeek && 'font-semibold text-(--color-brand)',
            )}
          >
            {start === currentWeek ? 'hoje' : formatDayMonth(start)}
          </span>
        ))}
      </div>
    </section>
  );
}

/** A semana: o título com a faixa de datas e o estado, e uma linha por material. */
function WeekGroup({
  week,
  deadline,
  id,
}: {
  readonly week: MaterialWeek;
  readonly deadline: MaterialsTimelineProps['deadline'];
  readonly id: string;
}) {
  const count = week.lines.length;
  return (
    <section aria-labelledby={`${id}-titulo`} className="flex flex-col">
      <div
        id={id}
        className="grid scroll-mt-4 grid-cols-[56px_minmax(0,1fr)_auto] items-baseline gap-x-3 border-b border-(--color-border-subtle) pt-5 pb-1.5 [&:first-child]:pt-3 xl:grid-cols-[72px_minmax(0,1fr)_auto]"
      >
        <h2 id={`${id}-titulo`} className="col-span-2 m-0 text-[15px] font-semibold">
          Semana de {formatDayMonthName(week.start)}
          <span className="font-condensed ml-2 text-[13px] font-normal text-(--color-content-muted)">
            {formatShortRange(week.start, week.end)}
          </span>
          {week.status === 'PAST' && (
            <span className="ml-2.5 text-[13px] font-medium text-(--color-state-danger)">
              vencida
            </span>
          )}
          {week.status === 'CURRENT' && (
            <span className="ml-2.5 text-[13px] font-medium text-(--color-brand)">esta semana</span>
          )}
        </h2>
        <span className="font-condensed text-right text-xs text-(--color-content-muted)">
          {count} {count === 1 ? 'material' : 'materiais'}
        </span>
      </div>
      {week.lines.map((line) => (
        <MaterialLine key={line.id} line={line} deadline={deadline(line)} />
      ))}
    </section>
  );
}

/** A data e o prazo, o material e onde cai, a quantidade e a janela do serviço. */
function MaterialLine({
  line,
  deadline,
}: {
  readonly line: PurchaseLineView;
  readonly deadline: MaterialDeadline;
}) {
  const tone =
    deadline.level === 'OVERDUE'
      ? 'text-(--color-state-danger)'
      : deadline.level === 'DUE_SOON'
        ? 'text-(--color-state-warn)'
        : '';
  const window =
    line.plannedStart !== null && line.plannedEnd !== null
      ? `serviço ${formatShortRange(line.plannedStart, line.plannedEnd)}`
      : line.dateFollowsItem
        ? 'sem item no cronograma'
        : 'data fixa';
  const fileQuantity =
    line.fileQuantity !== null && line.fileQuantity !== line.quantity
      ? formatPurchaseQuantity(line.fileQuantity, line.unit, false)
      : undefined;

  return (
    <div
      data-testid="linha-de-material"
      className="grid min-h-[42px] grid-cols-[56px_minmax(0,1fr)_auto] items-center gap-x-3 border-b border-(--color-border-subtle) py-1.5 xl:grid-cols-[72px_minmax(0,1fr)_auto]"
    >
      <div className={cn('font-condensed text-[13px] leading-tight', tone)}>
        <span className="tabular">{formatDayMonth(line.requiredDate)}</span>
        {deadline.level !== 'SCHEDULED' && (
          <small className="block text-[11px]">{describeDeadline(deadline.daysUntil)}</small>
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-px">
        <span className="text-[13.5px] leading-tight font-medium">{line.materialName}</span>
        <span className="font-condensed text-xs text-(--color-content-muted) lg:truncate">
          {line.floorName} · {line.disciplineName} ·{' '}
          {line.serviceName ?? (
            <span title="Linha sem serviço: vale para a frente inteira.">Frente inteira</span>
          )}
          <span
            className={cn(
              'tabular ml-2',
              line.materialCodeProvisional &&
                'text-(--color-state-warn) underline decoration-dotted underline-offset-[3px]',
            )}
            title={
              line.materialCodeProvisional
                ? 'Código provisório: o material ainda não tem SKU e não vira requisição até ser vinculado na tela de Materiais (RN-347).'
                : 'Código de compras'
            }
          >
            {line.materialCode}
          </span>
          {line.kind === 'SERVICO_TERCEIRIZADO' && (
            <span
              className="ml-2"
              title="Contratação de empreiteiro, e não compra de material (RN-349)."
            >
              terceirizado
            </span>
          )}
        </span>
      </div>
      <div className="font-condensed text-right text-[13.5px] leading-tight whitespace-nowrap">
        <span className="tabular">
          {formatPurchaseQuantity(line.quantity, line.unit, line.quantityProvisional)}
          {line.quantityProvisional && (
            <span
              className="ml-1.5 text-[11px] text-(--color-state-warn)"
              title={
                line.allocationBasis === null
                  ? 'Quantidade estimada: rateio ou levantamento incompleto (RN-345).'
                  : BASIS_HELP[line.allocationBasis]
              }
            >
              provisória
            </span>
          )}
        </span>
        <small
          className="tabular block text-[11px] text-(--color-content-muted)"
          title={
            line.dateFollowsItem
              ? 'A data acompanha o início do item do cronograma por mão de obra, menos a margem de chegada da obra (RN-342).'
              : 'A data não acompanha o cronograma por mão de obra: vale a que foi lançada.'
          }
        >
          {window}
          {fileQuantity !== undefined && (
            <span title="O número da planilha, guardado quando o quantitativo assumiu a linha (RN-452).">
              {' · '}planilha: {fileQuantity}
            </span>
          )}
          {line.origin !== 'QUANTITATIVO' && ` · ${ORIGIN_LABEL[line.origin].toLowerCase()}`}
        </small>
      </div>
    </div>
  );
}
